import { useSnackbar } from 'notistack'
import { useContext, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath, useNavigate } from 'react-router-dom'

import { useWorkspaceParam } from 'features/workspace/hooks/useWorkspaceParam'
import { useMutation } from 'providers/tanstack-query/useMutation'
import { ROUTES } from 'routes'
import { EndAbTestContext } from '../../../context/EndAbTestContext'
import { Schema } from '../../schema'
import { Data, END_AB_TEST, mapper, Variables } from './api'
import { UseEndAbTest } from './types'

export const useEndAbTest = (): UseEndAbTest => {
  const { workspaceId } = useWorkspaceParam()
  const navigate = useNavigate()
  const { t } = useTranslation('features/abTests', { keyPrefix: 'endTest' })
  const { enqueueSnackbar } = useSnackbar()
  const { setLoading, setOpen } = useContext(EndAbTestContext)

  const { isPending, mutateAsync } = useMutation<{ input: Variables }, Data>({
    mutation: END_AB_TEST,
    onCompleted: async (data: Data) => {
      const {
        endAbTest: { errors },
      } = data

      setOpen(false)
      navigate(generatePath(ROUTES.AB_TESTS.LIST, { workspaceId }))

      if (errors && errors.length > 0) {
        enqueueSnackbar(t('error'), { variant: 'error' })
      } else {
        enqueueSnackbar(t('success'), { variant: 'success' })
      }
    },
    queriesToInvalidate: ['getAbTests'],
  })

  useEffect(() => {
    setLoading(isPending)
  }, [isPending])

  const endAbTest = (input: Schema): void => void mutateAsync({ input: mapper(input) })

  return {
    endAbTest,
  }
}
